import { useForm } from "react-hook-form";
import { useNotification } from "../../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import { SignupForm } from "../../components/signup-form";
import { useTelegram } from "../../hooks/useTelegram";
import VerificationModal from "../../components/VerificationModal";
import { useState } from "react";
import TelegramLoginButton from "../../components/ui/TelegramLoginButton";

export default function Register() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: async (data) => {
      const errors = {};
      if (data.password !== data.confirmPassword) {
        errors.confirmPassword = { message: "Passwords do not match" };
      }
      if (data.password.length < 8) {
        errors.password = { message: "Password must be at least 8 characters" };
      }
      return { values: data, errors };
    },
  });

  const { add } = useNotification();
  const navigate = useNavigate();
  const { user: telegramUser, initData, isTelegram } = useTelegram();
  const [showModal, setShowModal] = useState(false);

  const onSubmit = async (data) => {
    const { confirmPassword, ...payload } = data;

    try {
      await client.post("/auth/register", payload);
      if (data.role === "RECRUITER") {
        setShowModal(true); // modal telling user to check email; after verification admin will review
      }
    } catch (err) {
      add(err.response?.data?.message || "Registration failed", "error");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    // After acknowledging the verification prompt, take the user to the home page
    navigate("/");
  };

  const handleTelegramWebAppRegister = async () => {
    if (!telegramUser || !initData) {
      add("Telegram user data missing. Are you inside Telegram?", "error");
      return;
    }

    try {
      const res = await client.post("/auth/telegram", { initData });
      add("Registration successful!", "success");
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);
      }
      navigate("/");
    } catch (err) {
      add(
        err.response?.data?.message || "Telegram registration failed",
        "error",
      );
    }
  };

  const handleWidgetRegister = async (user) => {
    try {
      const res = await client.post("/auth/telegram", { widgetData: user });
      add("Registration successful!", "success");
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);
      }
      navigate("/");
    } catch (err) {
      add(
        err.response?.data?.message || "Telegram widget registration failed",
        "error",
      );
    }
  };

  return (
    <div className="min-h-screen ">
      <div className="grid min-h-screen lg:grid-cols-2">
        {/* LEFT SIDE – BRAND & FEATURES */}
        <div className="hidden lg:flex flex-col justify-center p-8 lg:p-12 ">
          {/* Content */}
          <div className="max-w-lg mx-auto space-y-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Start your journey with{" "}
                <span className="text-primary">Shebeka</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Join thousands of professionals finding their dream jobs.
              </p>
            </div>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Connect with top recruiters</p>
                  <p className="text-sm text-muted-foreground">
                    Access opportunities from leading companies
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Track your applications</p>
                  <p className="text-sm text-muted-foreground">
                    Monitor your progress in real-time
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Get your dream job</p>
                  <p className="text-sm text-muted-foreground">
                    Land the role you've been looking for
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* RIGHT SIDE – FORM */}
        <div className="flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md space-y-6">
            <SignupForm
              onSubmit={handleSubmit(onSubmit)}
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
            />

            {/* Telegram Register Section */}
            {(isTelegram || !isTelegram) && (
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className=" px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                {isTelegram ? (
                  <button
                    onClick={handleTelegramWebAppRegister}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-sky-600 hover:bg-sky-600/90 text-white rounded-lg font-medium transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                    </svg>
                    Register with Telegram
                  </button>
                ) : (
                  <TelegramLoginButton
                    botName="shebekabot"
                    onAuth={handleWidgetRegister}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Verification Modal */}
      <VerificationModal isOpen={showModal} onClose={handleCloseModal} />
    </div>
  );
}
